import { COMPANY_NAME, COMPANY_DOMAIN, CONTACT_EMAIL, COMPANY_ADDRESS } from '../config';
import { useTranslation } from 'react-i18next';

const PricingPolicy = () => {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-3xl px-6 my-20">
      <div className="text-left font-sans">
        <h1>Pricing&nbsp;Policy</h1>

        {/* ───────────── 1. Core idea ───────────── */}
        <h2 className="mt-8">Preview First, Charge Second</h2>
        <p>
          Any time you change plans—upgrade, downgrade, or switch the billing cycle—we <strong>simulate</strong> the result <em>before</em> you pay.
          The dialog shows:
          <br />• any amount we’ll charge <em>now</em> (or credit we add to your balance)
          <br />• your new recurring price
          <br />• the exact date of your next invoice
          <br />
          Confirm the change and we apply it immediately. No hidden fees. Ever.
        </p>

        {/* ───────────── 2. Upgrades ───────────── */}
        <h2 className="mt-8">Upgrading a Plan</h2>
        <p>
          Moving to a higher tier (e.g. <strong>Startup → Professional</strong>) or to a longer billing cycle takes effect at once.
          We follow “fair proration”:
        </p>
        <ul className="list-disc ml-5 mt-4">
          <li>Unused time on your old plan becomes a credit.</li>
          <li>The credit is automatically deducted from today’s charge.</li>
          <li>Any leftover credit stays on your balance for future invoices.</li>
        </ul>

        {/* ───────────── 3. Downgrades to another paid tier ───────────── */}
        <h2 className="mt-8">Downgrading to a Cheaper Paid Plan</h2>
        <p>
          Switching from a higher‑priced tier to a lower‑priced one (e.g. <strong>Professional → Startup</strong>)
          is <strong>immediate</strong>. You lose the higher‑tier features right away.
          The unused portion of the current cycle is converted to account credit and will automatically apply to
          your next invoice.
        </p>

        {/* ───────────── 4. Downgrading to Free ───────────── */}
        <h2 className="mt-8">Downgrading to Free</h2>
        <p>
          If you leave a paid tier for <strong>Free</strong>, we queue the change for the end of the
          current billing period—you keep paid features until then. You can cancel the pending downgrade any time
          before the period ends.
        </p>

        {/* ───────────── 5. Examples ───────────── */}
        <h2 className="mt-8">Example&nbsp;1 — Upgrade with Immediate Charge</h2>
        <p>
          Alice is on <b>Startup · 1&nbsp;month ($59)</b>. Halfway through the month she upgrades to <b>Professional · 1&nbsp;month ($99)</b>.
          <br />• Unused Startup time → <b>$29.50 credit</b>
          <br />• Immediate charge → <b>$99 – 29.50&nbsp;=&nbsp;$69.50</b>
          <br />• New recurring → <b>$99 every month</b>, next invoice exactly one month from today.
        </p>

        <h2 className="mt-8">Example&nbsp;2 — Immediate Downgrade with Credit</h2>
        <p>
          Bob prepaid <b>Professional · 3&nbsp;months ($249)</b> but after one week realises <b>Startup · 3&nbsp;months ($149)</b> is enough.
          <br />• Unused Professional time → <b>≈ $229.63 credit</b>
          <br />• Cost of Startup → <b>$149</b>
          <br />• Balance after swap → <b>≈ $80.63 credit</b> carried forward to future invoices.
        </p>

        <h2 className="mt-8">Example&nbsp;3 — Scheduled Downgrade to Free</h2>
        <p>
          Carol is on <b>Professional · 1&nbsp;year</b> and schedules a downgrade to <b>Free</b>.
          Nothing changes right away; the downgrade will auto‑apply at the end of the paid year. Until then she enjoys full Professional access.
        </p>

        {/* ───────────── 6. Credit policy ───────────── */}
        <h2 className="mt-8">Account Credit Never Expires</h2>
        <p>
          Credits created by proration have no expiry date, apply automatically, and can’t be redeemed for cash.
        </p>

        {/* ───────────── 7. Transparency ───────────── */}
        <h2 className="mt-8">Fair&nbsp;&&nbsp;Transparent</h2>
        <p>
          You always see the exact numbers before confirming. Questions?&nbsp;
          <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">
            Contact support
          </a>&nbsp;— we’re happy to help.
        </p>
      </div>
    </div>
  );
};

export default PricingPolicy;